import { themeVars } from '@mds/ui/theme/themeVars';
import { Box, Chip, ListItemText } from '@mui/material';
import React from 'react';
import { AccountMenuItem, AccountMenuItemProps } from '../AccountMenuItem';

/**
 * Displays the current environment and app version in the account menu.
 *
 * The environment chip is hidden in production or when deployEnv is undefined.
 * The app version is shown by default in all environments and can be hidden via
 * `showVersionInProduction`.
 */
export function EnvironmentInfo({
  deployEnv,
  appVersion,
  showVersionInProduction = true,
  sx,
  ...rest
}: {
  /**
   * Normally DEV, UAT or PROD passed from environment variables. When PROD or
   * undefined, the environment chip is hidden but the app version is still shown
   * (unless `showVersionInProduction` is false).
   */
  deployEnv: string | 'PROD' | undefined;
  /**
   * Client app version, usually from package.json
   */
  appVersion: string;
  /**
   * Whether to show the app version string. Defaults to `true`.
   * Set to `false` to hide the version in production (or any environment).
   * On non-production environments the version is shown by default but can be
   * suppressed by passing `false`.
   */
  showVersionInProduction?: boolean;
} & AccountMenuItemProps) {
  const showEnvChip = !!deployEnv && deployEnv !== 'PROD';
  if (!showEnvChip && !showVersionInProduction) {
    return null;
  }
  return (
    <AccountMenuItem
      onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
      {...rest}
      // flexShrink: 0 overrides AccountMenuItem's own default flex: 0 (which shrinks: 1) -
      // this content sits in SideNav's toggle row, which is still narrower than its final
      // width mid-transition (AppSidebar only animates width; this mounts instantly). Without
      // it, the chip+text below get squeezed and wrap onto a second line for that instant,
      // then snap back to one line once the width transition catches up.
      sx={[{ marginTop: 'auto', flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <ListItemText
        primary={
          <>
            {showEnvChip && (
              <Chip label={deployEnv} size="small" variant="outlined" color="secondary" sx={{ mr: 2, flexShrink: 0 }} />
            )}
            {showVersionInProduction && (
              <Box
                component="span"
                sx={(theme) => ({ color: themeVars(theme).palette.text.secondary, whiteSpace: 'nowrap' })}
              >
                App version {appVersion}
              </Box>
            )}
          </>
        }
        slotProps={{ primary: { sx: { display: 'flex', alignItems: 'baseline', flexWrap: 'nowrap' } } }}
      />
    </AccountMenuItem>
  );
}
